import { query_dispatcher } from '@/api/client';

export interface SupplierSummary {
  id: number;
  cif: string;
  name: string;
  selectorText?: string;
  selector_text?: string;
  credit_account?: string;
}

interface ApiSupplierSummary {
  id: number;
  cif: string;
  name: string;
}

interface SupplierQueryDispatcher {
  dispatch(queryName: string, parameters: object): Promise<unknown>;
}

export const SUPPLIER_LOOKUP_ERROR_MESSAGE = 'No se pudo consultar el catálogo de proveedores.';

export const normalizeSupplierTaxId = (taxId: string): string => {
  const normalized = String(taxId || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return normalized.startsWith('ES') && normalized.length === 11
    ? normalized.slice(2)
    : normalized;
};

// Clave con que se compara un nombre: entero, sin mayusculas ni espacios de mas (como normalize_supplier_name).
export const normalizeSupplierName = (name: string): string => (
  String(name || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase()
);

export const filterSupplierListByName = (
  supplierList: SupplierSummary[], name: string,
): SupplierSummary[] => {
  const normalizedName = normalizeSupplierName(name);
  return supplierList.filter(
    supplier => normalizeSupplierName(supplier.name) === normalizedName,
  );
};

export const filterSupplierListByTaxId = (
  supplierList: SupplierSummary[], taxId: string,
): SupplierSummary[] => {
  const normalizedTaxId = normalizeSupplierTaxId(taxId);
  return supplierList.filter(
    supplier => normalizeSupplierTaxId(supplier.cif) === normalizedTaxId,
  );
};

export class ApiSupplierRepository {
  private supplierQueryDispatcher: SupplierQueryDispatcher;

  constructor(supplierQueryDispatcher: SupplierQueryDispatcher = query_dispatcher) {
    this.supplierQueryDispatcher = supplierQueryDispatcher;
  }

  private static parseSupplier(response: ApiSupplierSummary): SupplierSummary {
    return {
      id: response.id,
      cif: response.cif || '',
      name: response.name || '',
      selectorText: [response.name, response.cif].filter(Boolean).join(' — '),
    };
  }

  async search(
    companyId: number,
    searchTerm: string,
    limit = 20,
    offset = 0,
  ): Promise<SupplierSummary[]> {
    const response = await this.supplierQueryDispatcher.dispatch(
      'albaran_factura.supplier.search',
      {
        company_id: companyId,
        search_term: String(searchTerm || '').trim(),
        limit,
        offset,
      },
    );
    // Un error del servidor no es «ningun proveedor»: quien busca tiene que saber que fallo.
    if (!Array.isArray(response)) throw new Error('Supplier search failed');
    return response.map(ApiSupplierRepository.parseSupplier);
  }

  async findByTaxId(companyId: number, taxId: string): Promise<SupplierSummary[]> {
    const response = await this.supplierQueryDispatcher.dispatch(
      'albaran_factura.supplier.search',
      {
        company_id: companyId,
        search_term: normalizeSupplierTaxId(taxId),
        limit: 20,
        offset: 0,
      },
    );
    // Un error del servidor no es «ningun proveedor»: sin catalogo no se ofrece crear uno.
    if (!Array.isArray(response)) throw new Error('Supplier lookup failed');
    return filterSupplierListByTaxId(response.map(ApiSupplierRepository.parseSupplier), taxId);
  }

  // Solo el nombre entero enlaza: un trozo traeria cualquier ficha que lo contenga.
  async findByName(companyId: number, name: string): Promise<SupplierSummary[]> {
    const searchTerm = String(name || '').trim();
    if (!searchTerm) return [];
    const response = await this.supplierQueryDispatcher.dispatch(
      'albaran_factura.supplier.search',
      {
        company_id: companyId,
        search_term: searchTerm,
        limit: 20,
        offset: 0,
      },
    );
    if (!Array.isArray(response)) throw new Error('Supplier lookup failed');
    return filterSupplierListByName(response.map(ApiSupplierRepository.parseSupplier), name);
  }
}
